import Fuse, { IFuseOptions } from 'fuse.js';
import {
  SearchableItem,
  SearchResult,
  FuseSearchResult,
} from '../types/search';

/**
 * Fuse.js options for search configuration
 */
const FUSE_OPTIONS: IFuseOptions<SearchableItem> = {
  // Keys to search with relative weights
  keys: [
    { name: 'title', weight: 0.5 },
    { name: 'description', weight: 0.3 },
    { name: 'content', weight: 0.2 },
    { name: 'tags', weight: 0.15 },
  ],
  // Fuzzy matching threshold (0 = exact, 1 = match anything)
  threshold: 0.4,
  // Include score in results
  includeScore: true,
  // Include match data for highlighting
  includeMatches: true,
  // Ignore location of match in string
  ignoreLocation: true,
  // Don't require all terms to match
  useExtendedSearch: true,
  // Min characters before triggering search
  minMatchCharLength: 2,
};

/**
 * Maximum excerpt length for search results
 */
const MAX_EXCERPT_LENGTH = 150;

/**
 * Extract excerpt from content around the first match
 */
function extractExcerpt(
  content: string | undefined,
  matches: FuseSearchResult['matches'] | undefined,
  key: string
): string | undefined {
  if (!content) return undefined;

  // Find match for the specified key
  const match = matches?.find((m) => m.key === key);

  if (!match || !match.indices || match.indices.length === 0) {
    // No match found, return start of content
    if (content.length <= MAX_EXCERPT_LENGTH) return content;
    return content.slice(0, MAX_EXCERPT_LENGTH) + '...';
  }

  // Get the first match position
  const [start] = match.indices[0];

  // Calculate excerpt boundaries
  const excerptStart = Math.max(0, start - 40);
  const excerptEnd = Math.min(content.length, excerptStart + MAX_EXCERPT_LENGTH);

  let excerpt = content.slice(excerptStart, excerptEnd);

  // Add ellipsis if needed
  if (excerptStart > 0) excerpt = '...' + excerpt;
  if (excerptEnd < content.length) excerpt = excerpt + '...';

  return excerpt;
}

/**
 * Generate URL for a searchable item
 */
function getItemUrl(item: SearchableItem): string {
  switch (item.type) {
    case 'task':
      return `/tasks/${item.id}`;
    case 'page':
      return `/wiki/${item.slug}`;
    default:
      return '/';
  }
}

/**
 * Convert Fuse result to SearchResult format
 */
function toSearchResult(fuseResult: FuseSearchResult): SearchResult {
  const { item, score, matches } = fuseResult;

  // Determine the best excerpt source
  let excerpt: string | undefined;
  if (item.type === 'task' && item.description) {
    excerpt = extractExcerpt(item.description, matches, 'description');
  } else if (item.type === 'page' && item.content) {
    excerpt = extractExcerpt(item.content, matches, 'content');
  }

  return {
    type: item.type,
    id: item.id,
    title: item.title,
    excerpt,
    url: getItemUrl(item),
    // Fuse score is 0 (perfect) to 1 (worst), invert for intuitive use
    score: score !== undefined ? 1 - score : 1,
  };
}

/**
 * SearchIndex manages Fuse.js instances for searching tasks and pages
 */
export class SearchIndex {
  private fuse: Fuse<SearchableItem> | null = null;
  private items: SearchableItem[] = [];
  private isReady = false;

  /**
   * Check if the index is ready for searching
   */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * Get the number of items in the index
   */
  get size(): number {
    return this.items.length;
  }

  /**
   * Build or rebuild the search index with new data
   */
  buildIndex(items: SearchableItem[]): void {
    this.items = items;
    this.fuse = new Fuse(items, FUSE_OPTIONS);
    this.isReady = true;
  }

  /**
   * Clear the search index
   */
  clearIndex(): void {
    this.fuse = null;
    this.items = [];
    this.isReady = false;
  }

  /**
   * Search the index and return results
   */
  search(query: string, limit: number = 20): SearchResult[] {
    if (!this.fuse || !query.trim()) {
      return [];
    }

    const results = this.fuse.search(query, { limit }) as FuseSearchResult[];
    return results.map(toSearchResult);
  }

  /**
   * Search only tasks
   */
  searchTasks(query: string, limit: number = 10): SearchResult[] {
    if (!this.fuse || !query.trim()) {
      return [];
    }

    const results = this.fuse.search(query) as FuseSearchResult[];
    return results
      .filter((r) => r.item.type === 'task')
      .slice(0, limit)
      .map(toSearchResult);
  }

  /**
   * Search only pages
   */
  searchPages(query: string, limit: number = 10): SearchResult[] {
    if (!this.fuse || !query.trim()) {
      return [];
    }

    const results = this.fuse.search(query) as FuseSearchResult[];
    return results
      .filter((r) => r.item.type === 'page')
      .slice(0, limit)
      .map(toSearchResult);
  }
}

// Export singleton instance
export const searchIndex = new SearchIndex();
